import { singleline } from "@core/foundation/string";
import type { ReadonlyRecord } from "@core/foundation/types";
import type { ZodType } from "@core/zod";
import { z } from "@core/zod";

import { TagIdSchema } from "./tag.ts";
import type { TagKindId } from "./tag-kind.ts";
import { TagKindIdSchema } from "./tag-kind.ts";

const FolderConditionSchema = z.discriminatedUnion("kind", [
  z.object(
    { kind: z.literal("equal"), tagId: TagIdSchema },
    {
      kind: "Narrows the folder to the records carrying the tag.",
      tagId: "The id of the tag, which must be of the condition's kind.",
    },
  ),
  z.object(
    { kind: z.literal("expansion") },
    { kind: "Gives the folder a subfolder per tag of the condition's kind." },
  ),
]);

type FolderCondition = z.infer<typeof FolderConditionSchema>;

export interface FolderDefinition {
  readonly conditions: ReadonlyRecord<TagKindId, FolderCondition>;
  readonly name: string;
  readonly subfolders: readonly FolderDefinition[];
}

const FolderDefinitionSchema: ZodType<FolderDefinition> = z.object(
  {
    conditions: z.record(TagKindIdSchema, FolderConditionSchema),
    name: z.string(),
    get subfolders() {
      return z.array(FolderDefinitionSchema);
    },
  },
  {
    conditions: singleline`
      The folder's conditions, keyed by the id of the tag kind each applies to,
      with at most one expansion.
    `,
    name: "The folder's name.",
    subfolders: "The folders inside this one, each inheriting its tags.",
  },
);

export const FolderStructureSchema = z.object(
  { folders: z.array(FolderDefinitionSchema) },
  { folders: "The structure's top folders." },
);
